<template>
    <div>
        <app-template>
            <modal name="export-cerfa-csv" height="auto" :adaptive="true">
                <div class="p-6 space-y-4 overflow-y-auto max-h-[100vh]">
                    <h2 class="text-lg font-bold">
                        Export des CERFA (CSV)
                    </h2>

                    <!-- FILTRES -->
                    <div class="flex flex-col gap-4">

                        <!-- Intervention -->
                        <input
                            v-model="filtersExport.intervention"
                            type="text"
                            placeholder="Intervention"
                            class="border rounded px-3 py-2"
                        />

                        <!-- Technicien -->
                        <select
                            v-model="filtersExport.technicien_id"
                            class="border rounded px-3 py-2"
                        >
                            <option value="">Technicien</option>
                            <option v-for="technicien in techniciens" :key="technicien.id" :value="technicien.id">
                                {{ technicien.prenom }} {{ technicien.nom }}
                            </option>
                        </select>

                        <!-- Client -->
                        <input
                            v-model="filtersExport.client"
                            type="text"
                            placeholder="Client"
                            class="border rounded px-3 py-2"
                        />

                        <!-- Fluide -->
                        <input
                            v-model="filtersExport.fluide"
                            type="text"
                            placeholder="Fluide"
                            class="border rounded px-3 py-2"
                        />

                        <!-- Date -->
                        <input
                            v-model="filtersExport.created_at"
                            type="date"
                            class="border rounded px-3 py-2"
                        />

                        <!-- Signature client -->
                        <select
                            v-model="filtersExport.signed"
                            class="border rounded px-3 py-2"
                        >
                            <option value="">Signature client</option>
                            <option :value="1">Signé</option>
                            <option :value="0">Non signé</option>
                        </select>

                    </div>

                    <div class="flex justify-end gap-3 pt-4">
                        <button
                            class="px-4 py-2 rounded border"
                            @click="$modal.hide('export-cerfa-csv')"
                        >
                            Annuler
                        </button>

                        <button
                            class="bg-green-600 text-white px-4 py-2 rounded flex items-center"
                            @click="exportCerfaCsv"
                            :disabled="loadingExportCerfa"
                        >
                            <i v-if="loadingExportCerfa" class="fas fa-spinner fa-spin mr-2"></i>
                            Exporter
                        </button>
                    </div>
                </div>
            </modal>

            <div class="space-y-4">
                <div class="flex justify-end">
                    <button v-if="!$account.account.societe.solo" class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center" @click="openExportCerfaModal">
                        <i class="fas fa-file-csv mr-2"></i>
                        Exporter
                    </button>
                </div>

                <!-- FILTRES -->
                <div class="bg-white p-4 rounded shadow grid grid-cols-1 md:grid-cols-6 gap-4">

                    <!-- Intervention -->
                    <input
                        v-model="filters.intervention"
                        type="text"
                        placeholder="Intervention"
                        class="border rounded px-3 py-2"
                    />

                    <!-- Technicien -->
                    <select
                        v-model="filters.technicien_id"
                        class="border rounded px-3 py-2"
                    >
                        <option value="">Technicien</option>
                        <option v-for="technicien in techniciens" :key="technicien.id" :value="technicien.id">
                            {{ technicien.prenom }} {{ technicien.nom }}
                        </option>
                    </select>

                    <!-- Client -->
                    <input
                        v-model="filters.client"
                        type="text"
                        placeholder="Client"
                        class="border rounded px-3 py-2"
                    />

                    <!-- Fluide -->
                    <input
                        v-model="filters.fluide"
                        type="text"
                        placeholder="Fluide"
                        class="border rounded px-3 py-2"
                    />

                    <!-- Date -->
                    <input
                        v-model="filters.created_at"
                        type="date"
                        class="border rounded px-3 py-2"
                    />

                    <!-- Signature client -->
                    <select
                        v-model="filters.signed"
                        class="border rounded px-3 py-2"
                    >
                        <option value="">Signature client</option>
                        <option :value="1">Signé</option>
                        <option :value="0">Non signé</option>
                    </select>

                </div>

                <!-- TABLE -->
                <div class="bg-white rounded shadow overflow-x-auto">
                    <table class="min-w-full text-sm">
                        <thead class="bg-gray-100 text-left">
                            <tr>
                                <th class="p-3">Intervention</th>
                                <th class="p-3">Technicien</th>
                                <th class="p-3">Client</th>
                                <th class="p-3">Fluide</th>
                                <th class="p-3">Créé le</th>
                                <th class="p-3">Signé par le client</th>
                                <th class="p-3">PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loading">
                                <td colspan="6" class="p-4 text-center text-gray-500">
                                    Chargement…
                                </td>
                            </tr>

                            <template v-if="!loading">
                                <tr v-for="cerfa in cerfas" :key="cerfa.id" class="border-t">
                                    <td class="p-3">{{ cerfa.intervention.reference }}</td>
                                    <td class="p-3">{{ cerfa.intervention.technicien.prenom + ' ' + cerfa.intervention.technicien.nom }}</td>
                                    <td class="p-3">{{ cerfa.intervention.client.nom + ' ' + cerfa.intervention.client.prenom }}</td>
                                    <td class="p-3">{{ cerfa.fluide_denomination || '-' }}</td>
                                    <td class="p-3">{{ formatDate(cerfa.created_at) }}</td>
                                    <td class="p-3">
                                        <i :class="cerfa.intervention.signature_client ? 'fas fa-check-circle text-green-500' : 'fas fa-times-circle text-gray-500'"></i>
                                    </td>
                                    <td class="p-3">
                                        <a
                                            v-if="cerfa.document_url"
                                            :href="cerfa.document_url"
                                            target="_blank"
                                            class="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                        >
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                            PDF
                                        </a>
                                        <span v-else class="text-gray-400">—</span>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>

                <!-- PAGINATION -->
                <div class="flex items-center justify-between">
                    <button
                        :disabled="pagination.page === 1"
                        @click="changePage(pagination.page - 1)"
                        class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                        Précédent
                    </button>

                    <span>
                        Page {{ pagination.page }} / {{ pagination.last_page }}
                    </span>

                    <button
                        :disabled="pagination.page === pagination.last_page"
                        @click="changePage(pagination.page + 1)"
                        class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                        Suivant
                    </button>
                </div>
            </div>
        </app-template>
    </div>
</template>

<script>
import axios from 'axios'
import env from '@/services/env.service'

export default {
    data()
    {
        return {
            loading: false,
            cerfas: [],
            techniciens: [],
            filters: {
                intervention: '',
                technicien_id: '',
                client: '',
                fluide: '',
                created_at: '',
                signed: '',
            },
            filtersExport: {
                intervention: '',
                technicien_id: '',
                client: '',
                fluide: '',
                created_at: '',
                signed: '',
            },
            pagination: {
                page: 1,
                per_page: 20,
                last_page: 1,
            },
            loadingExportCerfa: false,
        };
    },

    watch: {
        filters: {
            deep: true,
            handler() {
                this.pagination.page = 1;
                this.fetchCerfas();
            },
        },
    },

    methods: {
        fetchCerfas()
        {
            this.loading = true;

            this.$request.post('/cerfa/list', {
                ...this.filters,
                page: this.pagination.page,
                per_page: this.pagination.per_page,
            }).then(res => {
                this.cerfas = res.data;
                this.pagination.last_page = res.last_page;
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },

        async fetchTechniciens()
        {
            try {
                const res = await this.$request.get('/techniciens');
                this.techniciens = res;
            } catch (error) {
                console.error('Erreur lors de la récupération des techniciens:', error);
            }
        },

        changePage(page)
        {
            this.pagination.page = page;
            this.fetchCerfas();
        },

        formatDate(date)
        {
            return date ? new Date(date).toLocaleDateString() : '-';
        },

        openExportCerfaModal()
        {
            this.filtersExport = { ...this.filters };
            this.$modal.show('export-cerfa-csv');
        },

        async exportCerfaCsv()
        {
            this.loadingExportCerfa = true;

            try {
                const response = await axios.post(
                    env.apiUrl + '/cerfa/export/csv',
                    {
                        ...this.filtersExport,
                    },
                    {
                        responseType: 'blob',
                        headers: {
                            'Authorization': 'Bearer ' + env.authToken,
                        }
                    }
                );

                const blob = new Blob(
                    [response.data],
                    { type: 'text/csv;charset=utf-8;' }
                );

                const url = window.URL.createObjectURL(blob);

                let fileName = 'cerfas_export.csv';
                const cd = response.headers['content-disposition'];
                if (cd) {
                    const match = cd.match(/filename="?([^"]+)"?/);
                    if (match) fileName = match[1];
                }

                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(url);

                this.$toast.success('Export CERFA généré avec succès');
                this.$modal.hide('export-cerfa-csv');

            } catch (e) {
                console.error(e);
                this.$toast.error('Erreur lors de la génération du CSV');
            }

            this.loadingExportCerfa = false;
        },
    },

    mounted() {
        this.fetchCerfas();
        this.fetchTechniciens();
    },
};
</script>